import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from "react";
import { Box, Stack, Button, Typography } from "@curves/ui-base";
import { EmployeeSelect } from "@icoach/components";
import MemberSetting from "@icoach/pos/components/MemberSetting";
import InvoiceSetting from "@icoach/pos/components/InvoiceSetting";
import { useInvoiceState } from "@icoach/pos/hooks/useInvoiceState";
import { isEmpty, isEqual, isFunction, refIsRequiredError } from "@util/utils";
import useInvoiceApi from "@apis/useInvoiceApi";
import { INVOICE_ISSUING_TYPE } from "@constants/";
import CampaignSetting from "@icoach/pos/components/CampaignSetting";

const TransactionInfo = forwardRef(({
	dialogRef, options, onDialogStateChange, onCampaignChange, selectedCampaign, hasCartItems = false,
	onMemberChange, appliedVoucher, isApplyingVoucher = false,
	onClearVoucher, onRemoveVoucher,
}, ref) => {
	// 從 options 中解構出需要的選項
	const { peopleOption = [], employeeOption = [], invoiceTypeOption = [], invoiceCarrierTypeOption = [] } = options || {};

	const { getMemberInvoiceApi } = useInvoiceApi();

	// 會員狀態
	const [selectedMember, setSelectedMember] = useState(null);

	const memberRef = useRef(null);
	const campaignRef = useRef(null);
	const employeeRef = useRef(null);

	// 使用發票狀態管理 Hook
	const { invoiceData: selectInvoice, updateInvoiceData: setSelectInvoice } = useInvoiceState(null, {
		invoiceTypeOption,
		invoiceCarrierTypeOption,
	});

	// 載入會員預設發票設定
	const getMemberDefaultInvoiceApi = async memberID => {
		try {
			const resp = await getMemberInvoiceApi({ memberID });
			if (resp) {
				const { issuingType } = resp || {};

				if (isEqual(INVOICE_ISSUING_TYPE.DUPLICATEUNIFORMINVOICE, issuingType) || isEqual(INVOICE_ISSUING_TYPE.TRIPLICATEUNIFORMINVOICE, issuingType)) {
					setSelectInvoice({
						issuingType,
						issuingTypeText: resp.issuingTypeText,
						invoiceCarrierType: resp.invoiceCarrierType,
						invoiceCarrierTypeText: resp.invoiceCarrierTypeText,
						invoiceCarrierNo: resp.invoiceCarrierNo,
						taxID: resp.taxID,
						taxTitle: resp.taxTitle,
						issuingEmail: resp.issuingEmail,
					});
				} else {
					setSelectInvoice(null);
				}
			} else {
				setSelectInvoice(null);
			}
		} catch (error) {}
	};

	// 處理會員選擇
	const handleMemberChange = member => {
		setSelectedMember(member);
	};

	// 處理抵用券選擇
	const handleCampaignChange = campaignID => {
		// 通知父組件抵用券變更（父組件管理狀態）
		if (onCampaignChange && typeof onCampaignChange === "function") {
			onCampaignChange(campaignID);
		}
	};

	const handleInvoiceChange = data => {
		setSelectInvoice(data);
	};

	const handleSetInvoiceClick = (sourceData, refresh) => {
		const { openInvoiceSettingDialog } = dialogRef.current || {};
		isFunction(openInvoiceSettingDialog) && openInvoiceSettingDialog(sourceData, refresh);
	};

	// 提供 getResult 方法給父組件
	useImperativeHandle(
		ref,
		() => ({
			getResult: () => ({
				member: selectedMember,
				invoice: selectInvoice,
				campaignID: selectedCampaign,
				employeeID: employeeRef.current.getResult() || 0,
			}),
			isError: () => {
				return refIsRequiredError(memberRef, employeeRef);
			},
			// 抵用券與序號由父組件管理，清空時同步通知。
			clearAll: () => {
				setSelectedMember(null);
				setSelectInvoice(null);
				onClearVoucher?.();
				onCampaignChange(null);
			},
			// 提供設定會員的方法給父組件
			setMember: (member) => {
				setSelectedMember(member);
			},
		}),
		// eslint-disable-next-line
		[selectedMember, selectInvoice, selectedCampaign, onClearVoucher, onCampaignChange],
	);

	// ref 更新後再通知父層，讓折扣試算取得最新的會員。
	useEffect(() => {
		onMemberChange?.(selectedMember?.id || 0);
	}, [selectedMember, onMemberChange]);

	// 當會員選擇變更時，載入預設發票資訊
	useEffect(
		() => {
			if (selectedMember && selectedMember.id) {
				getMemberDefaultInvoiceApi(selectedMember?.id);
			} else if (!appliedVoucher) {
				onCampaignChange(null);
			}
		},
		// eslint-disable-next-line
		[selectedMember],
	);

	return (
		<Box className="pos-order-info">
			{/* 會員資訊 */}
			<Stack className="detail-row" alignItems="center">
				<label>會員</label>
				<MemberSetting ref={memberRef} memberData={selectedMember} onChange={handleMemberChange} peopleOption={peopleOption} enableScan onDialogStateChange={onDialogStateChange} />
			</Stack>
			{/* 發票設定 - 只有選擇會員後才顯示 */}
			{!isEmpty(selectedMember) && (
				<Stack className="detail-row" alignItems="center">
					<label>發票設定</label>
					<InvoiceSetting
						invoiceData={selectInvoice}
						onEditClick={() =>
							handleSetInvoiceClick(
								{
									...selectInvoice,
									invoiceTypeOption,
									invoiceCarrierTypeOption,
								},
								handleInvoiceChange,
							)
						}
					/>
				</Stack>
			)}
			{/* 抵用劵 - 只有選擇會員且購物車有商品時才顯示 */}
			{!isEmpty(selectedMember) && hasCartItems && (
				<Stack className="detail-row" alignItems="center">
					<label>抵用劵</label>
					<CampaignSetting ref={campaignRef} options={options?.campaignOptions || []} campaignID={selectedCampaign} onChange={handleCampaignChange} onManualChange={onClearVoucher} disabled={isApplyingVoucher} />
				</Stack>
			)}
			{appliedVoucher && (
				<Stack className="mb-2" spacing={1}>
					<Stack direction="row" spacing={1} alignItems="center">
						<Typography>已套用：{appliedVoucher.itemName}（{appliedVoucher.code}）</Typography>
						<Button type="button" onClick={onRemoveVoucher} disabled={isApplyingVoucher}>移除</Button>
					</Stack>
					{(isEmpty(selectedMember) || !hasCartItems) && <Typography>請先選擇會員並加入商品</Typography>}
				</Stack>
			)}
			{/* 結帳人員 */}
			<Stack className="detail-row" justifyContent="space-between" alignItems="center">
				<label>結帳人員</label>
				<EmployeeSelect ref={employeeRef} label={null} options={employeeOption} required />
			</Stack>
		</Box>
	);
});

export default TransactionInfo;
